import { z } from 'zod';

/** Cycle the press runs: load → cure → unload → idle. `held` = parked by a supervisor hold command. */
export const CYCLE_STATES = ['load', 'cure', 'unload', 'idle', 'held'] as const;
export const CycleState = z.enum(CYCLE_STATES);
export type CycleState = z.infer<typeof CycleState>;

/** Numeric signals stored as columns of the wide telemetry table. */
export const NUMERIC_SIGNALS = [
  'platen_temp',
  'steam_pressure',
  'hydraulic_pressure',
  'cycle_count',
  'energy_kwh',
  'motor_current',
] as const;
export type NumericSignal = (typeof NUMERIC_SIGNALS)[number];

export const SIGNAL_META: Record<NumericSignal, { label: string; unit: string; min: number; max: number }> = {
  platen_temp: { label: 'Platen temperature', unit: '°C', min: -40, max: 400 },
  steam_pressure: { label: 'Steam pressure', unit: 'bar', min: 0, max: 40 },
  hydraulic_pressure: { label: 'Hydraulic pressure', unit: 'bar', min: 0, max: 400 },
  cycle_count: { label: 'Cycle count', unit: '', min: 0, max: 1e9 },
  energy_kwh: { label: 'Energy', unit: 'kWh', min: 0, max: 1e9 },
  motor_current: { label: 'Motor current', unit: 'A', min: 0, max: 500 },
};

const signalNumber = (s: NumericSignal) =>
  z.number({ invalid_type_error: `${s} must be a number` }).finite().min(SIGNAL_META[s].min, `${s} below range`).max(SIGNAL_META[s].max, `${s} above range`);

export const Signals = z
  .object({
    platen_temp: signalNumber('platen_temp'),
    steam_pressure: signalNumber('steam_pressure'),
    hydraulic_pressure: signalNumber('hydraulic_pressure'),
    cycle_count: signalNumber('cycle_count').int(),
    energy_kwh: signalNumber('energy_kwh'),
    motor_current: signalNumber('motor_current'),
  })
  .strict();
export type Signals = z.infer<typeof Signals>;

const PressId = z.string().regex(/^[A-Za-z0-9-]{1,32}$/, 'invalid press id');
/** Epoch milliseconds, between 2020 and 2100. */
const EpochMs = z.number().int().min(1_577_836_800_000).max(4_102_444_800_000);

/** press/{id}/data — once per second, QoS 1, not retained. */
export const DataMessage = z
  .object({
    v: z.literal(1),
    pressId: PressId,
    ts: EpochMs,
    seq: z.number().int().nonnegative(),
    sessionId: z.string().min(1).max(64),
    state: CycleState,
    signals: Signals,
  })
  .strict();
export type DataMessage = z.infer<typeof DataMessage>;

/** press/{id}/state — on every state change, QoS 1, retained. */
export const StateMessage = z
  .object({ v: z.literal(1), pressId: PressId, ts: EpochMs, seq: z.number().int().nonnegative(), state: CycleState, prev: CycleState.nullable() })
  .strict();
export type StateMessage = z.infer<typeof StateMessage>;

export const PressBirth = z
  .object({ v: z.literal(1), pressId: PressId, ts: EpochMs, sessionId: z.string(), signals: z.array(z.string()) })
  .strict();
export type PressBirth = z.infer<typeof PressBirth>;

export const PressDeath = z.object({ v: z.literal(1), pressId: PressId, ts: EpochMs, reason: z.string() }).strict();
export type PressDeath = z.infer<typeof PressDeath>;

export const GatewayBirth = z
  .object({ v: z.literal(1), gatewayId: z.string(), ts: EpochMs, sessionId: z.string(), presses: z.array(PressId) })
  .strict();
export type GatewayBirth = z.infer<typeof GatewayBirth>;

/**
 * Last-will and clean-stop death. `ts` is optional because a will payload is fixed at connect time.
 * `sessionId` (optional, additive) is the session of the connection this death belongs to, so a consumer can
 * match it to the gateway birth with the same `sessionId` whatever order the retained messages arrive in.
 */
export const GatewayDeath = z
  .object({ v: z.literal(1), gatewayId: z.string(), ts: EpochMs.optional(), sessionId: z.string().optional(), reason: z.string() })
  .strict();
export type GatewayDeath = z.infer<typeof GatewayDeath>;

export const COMMAND_TYPES = ['hold', 'resume', 'reset_counter'] as const;
export const CommandType = z.enum(COMMAND_TYPES);
export type CommandType = z.infer<typeof CommandType>;

/** press/{id}/cmd — backend → gateway, QoS 1. */
export const CommandMessage = z
  .object({ v: z.literal(1), commandId: z.string().uuid(), pressId: PressId, type: CommandType, issuedAt: EpochMs, expiresAt: EpochMs })
  .strict();
export type CommandMessage = z.infer<typeof CommandMessage>;

/** press/{id}/cmd/ack — gateway → backend, QoS 1. */
export const CommandAck = z
  .object({
    v: z.literal(1),
    commandId: z.string().uuid(),
    pressId: PressId,
    result: z.enum(['acknowledged', 'failed']),
    reason: z.string().optional(),
    ts: EpochMs,
  })
  .strict();
export type CommandAck = z.infer<typeof CommandAck>;

/** Turn a Zod error into one short human-readable reason for logs. */
export function describeZodError(err: z.ZodError): string {
  return err.issues
    .slice(0, 3)
    .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
    .join('; ');
}
